// TBH: No idea why this would be a rule. Might be if developers are doing weird stuff with the code...
/* eslint-disable jsx-a11y/mouse-events-have-key-events */

import { forwardRef, HTMLAttributes, KeyboardEvent, MouseEvent, ReactNode, Ref, TouchEvent } from 'react';

/**
 * ## @visiba/cellula-button
 *
 * CellulaHeadlessButton
 */
export type HeadlessButtonType = 'submit' | 'reset' | 'button';
export interface HeadlessButtonProps extends HTMLAttributes<HTMLButtonElement> {
	/**
	 * Indicate if the button is disabled. It will not make the button natively
	 * disabled, instead it will add `aria-disabled="true"` and disable all user
	 * interaction events (it can be bypassed by `allowPassThrough`).
	 */
	disabled?: boolean;

	/**
	 * Use to indicate if the button is busy or not. It will add
	 * `aria-busy="true"` and disable all user interaction events (it can be
	 * bypassed by `allowPassThrough`).
	 */
	busy?: boolean;

	/**
	 * Reflects the value of the native HTML Element `class` property.
	 */
	className?: string;

	/**
	 * Reflects the value of the native HTML Button `type` property.
	 * @default button
	 */
	type?: HeadlessButtonType;

	/**
	 * The content in the button.
	 */
	children?: ReactNode;

	/**
	 * Use this to always run user interaction events when when the button is
	 * disabled or in loading state.
	 */
	allowPassThrough?: boolean;

	/**
	 * Reflects the value of the native HTML Element `form` property.
	 */
	form?: string;
}

function CellulaHeadlessButton(
	{
		children,
		className,
		type = 'button',
		disabled,
		busy = false,
		allowPassThrough,
		// Takes out mouse events for override.
		onClick,
		onMouseDown,
		onMouseUp,
		onMouseEnter,
		onMouseOver,
		onMouseLeave,
		// Takes out keyboard events for override.
		onKeyUp,
		onKeyDown,
		// Takes out touch events for override.
		onTouchEnd,
		onTouchMove,
		onTouchStart,
		onMouseMove,
		...rest
	}: HeadlessButtonProps,
	ref: Ref<HTMLButtonElement>,
): JSX.Element {
	// Mouse events
	function handleClick(event: MouseEvent<HTMLButtonElement>): void {
		if ((!disabled || allowPassThrough) && onClick) onClick(event);
	}

	function handleMouseDown(event: MouseEvent<HTMLButtonElement>): void {
		if ((!disabled || allowPassThrough) && onMouseDown) onMouseDown(event);
	}
	function handleMouseUp(event: MouseEvent<HTMLButtonElement>): void {
		if ((!disabled || allowPassThrough) && onMouseUp) onMouseUp(event);
	}

	function handleMouseEnter(event: MouseEvent<HTMLButtonElement>): void {
		if ((!disabled || allowPassThrough) && onMouseEnter) onMouseEnter(event);
	}

	function handleMouseLeave(event: MouseEvent<HTMLButtonElement>): void {
		if ((!disabled || allowPassThrough) && onMouseLeave) onMouseLeave(event);
	}

	function handleMouseMove(event: MouseEvent<HTMLButtonElement>): void {
		if ((!disabled || allowPassThrough) && onMouseMove) onMouseMove(event);
	}

	function handleMouseOver(event: MouseEvent<HTMLButtonElement>): void {
		if ((!disabled || allowPassThrough) && onMouseOver) onMouseOver(event);
	}

	// Keyboard events
	function handleKeyDown(event: KeyboardEvent<HTMLButtonElement>): void {
		if ((!disabled || allowPassThrough) && onKeyDown) onKeyDown(event);
	}

	function handleKeyUp(event: KeyboardEvent<HTMLButtonElement>): void {
		if ((!disabled || allowPassThrough) && onKeyUp) onKeyUp(event);
	}

	// Touch events
	function handleTouchStart(event: TouchEvent<HTMLButtonElement>): void {
		if ((!disabled || allowPassThrough) && onTouchStart) onTouchStart(event);
	}

	function handleTouchEnd(event: TouchEvent<HTMLButtonElement>): void {
		if ((!disabled || allowPassThrough) && onTouchEnd) onTouchEnd(event);
	}

	function handleTouchMove(event: TouchEvent<HTMLButtonElement>): void {
		if ((!disabled || allowPassThrough) && onTouchMove) onTouchMove(event);
	}

	return (
		// eslint-disable-next-line jsx-a11y/mouse-events-have-key-events
		<button
			className={className}
			aria-disabled={disabled ? 'true' : undefined}
			aria-busy={busy ? 'true' : undefined}
			onClick={handleClick}
			onKeyDown={handleKeyDown}
			onKeyUp={handleKeyUp}
			onMouseDown={handleMouseDown}
			onMouseMove={handleMouseMove}
			onMouseEnter={handleMouseEnter}
			onMouseLeave={handleMouseLeave}
			onMouseUp={handleMouseUp}
			onTouchEnd={handleTouchEnd}
			onTouchMove={handleTouchMove}
			onTouchStart={handleTouchStart}
			onMouseOver={handleMouseOver}
			// TODO: See if it's safe to add forceUpdate as a dependency
			type={type}
			{...rest}
			ref={ref}
		>
			{children}
		</button>
	);
}

const HeadlessButton = forwardRef(CellulaHeadlessButton);

export { HeadlessButton };
